import { queryClient } from '../lib/queryClient';
import { invalidateTaskLists, removeTaskFromCachedLists } from './task/taskApi';
import { invalidateTitleLists } from './titles/titlesApi';

/**
 * The backend applies a workflow's result through an event a few seconds after
 * MSR2 answers, so a refetch on close alone can still return the old lists.
 * The later passes catch that gap.
 */
export const EMBEDDED_WORKFLOW_REFRESH_DELAYS_MS = [3_000, 6_000, 10_000];

const LAST_DELAY_MS = Math.max(...EMBEDDED_WORKFLOW_REFRESH_DELAYS_MS);

let pendingTimers: ReturnType<typeof setTimeout>[] = [];

// Deleted drafts' tasks. The backend removes them later, so a pass's refetch
// can still return one; each pass drops them again once it has refetched, and
// the last pass forgets them.
const removedTaskIds = new Set<string>();

// The task lists, the task filter options and the title lists all bypass the
// axios cache (`fetchTasksPaginated`, `fetchTaskFilterOptions`,
// `fetchTitlesPaginated`), so an invalidation refetches from the server.
const refreshTaskAndTitleLists = async () => {
  await Promise.all([
    invalidateTaskLists(queryClient),
    invalidateTitleLists(queryClient),
  ]);
  removedTaskIds.forEach((taskId) =>
    removeTaskFromCachedLists(queryClient, taskId),
  );
};

/**
 * Drops a deleted draft's task from the cached lists and keeps it out of every
 * pass a workflow close could still have pending. The passes keep running for
 * the workflow's own changes.
 */
export const removeTaskFromListsDuringRefreshes = (taskId: string) => {
  removeTaskFromCachedLists(queryClient, taskId);
  removedTaskIds.add(taskId);
};

/**
 * Refetches the task and title lists now and again after each delay. Lives
 * outside any component because the lists remount when the embed closes. A
 * new call replaces the passes a previous one still had pending.
 */
export const refreshListsAfterEmbeddedWorkflow = () => {
  pendingTimers.forEach(clearTimeout);
  refreshTaskAndTitleLists();

  pendingTimers = EMBEDDED_WORKFLOW_REFRESH_DELAYS_MS.map((delay) =>
    setTimeout(async () => {
      await refreshTaskAndTitleLists();
      if (delay === LAST_DELAY_MS) removedTaskIds.clear();
    }, delay),
  );
};
